import type { ReactNode } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from './Button';
import { cn } from './cn';

export interface FilterBarProps {
  /** Filters / search controls (left, wraps on small screens). */
  children: ReactNode;
  /** Right-aligned actions (e.g. Export, New). */
  actions?: ReactNode;
  className?: string;
}

/**
 * A consistent toolbar for a list page's filters + actions. Wraps gracefully on
 * mobile (filters stack, actions drop below) instead of overflowing the row — the
 * audit flagged fixed-width Select rows overflowing at 320–425px across the app.
 */
export function FilterBar({ children, actions, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface FilterResetButtonProps {
  onClick: () => void;
  /** Filters currently applied, printed as "(n)". Leave it off where the count is not known. */
  count?: number;
  /** Match the controls beside it: `md` (36px) next to default-height inputs/selects, `sm` (32px)
   *  next to size="sm" ones and in empty states. */
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

/**
 * THE button that undoes a list's filters — every page uses this one.
 *
 * It had drifted into six looks (an Eraser "Clear" under a red count badge, a plain text "Clear", an
 * XCircle / FilterX "Clear filters", a hand-rolled RotateCcw "Reset"), so the same act read as a
 * different control on every screen. One look: bordered, in the brand tint, with a reset glyph —
 * the way out of a filtered view is the thing the eye lands on. With nothing to undo it fades to a
 * disabled text button. `!` on the tint because cn() has no tailwind-merge and the `default`
 * variant's own bg/border/text would otherwise win or lose by stylesheet order.
 */
export function FilterResetButton({ onClick, count, size = 'sm', disabled, className }: FilterResetButtonProps) {
  return (
    <Button
      size={size}
      variant={disabled ? 'text' : 'default'}
      icon={<RotateCcw size={15} />}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'shrink-0',
        !disabled && 'border-brand-500/45! bg-brand-500/8! text-brand-700! hover:bg-brand-500/15! dark:text-brand-300!',
        className,
      )}
    >
      Reset filters{count ? ` (${count})` : ''}
    </Button>
  );
}
